import { inject, observer } from "mobx-react";
import { useEffect, useRef, useCallback, useState } from "react";
import * as PlayerJs from "player.js";

import { ListenLive, ListenLiveEvent } from "../lib/listenLive";
import { ILog } from "@ihr-radioedit/inferno-core";
import { Toast } from "../ui";
import { MagicLink } from "@inferno/renderer-shared-ui";
import { AppTrayInjector } from "./AppTrayInjector";
import styles from "./IHRPlayerBar.module.scss";
import type { Store } from "@inferno/renderer-shared-core";
import { useTranslation } from "react-i18next";

const log = ILog.logger("IHRPlayerBar.component.tsx");

interface IHRPlayerBarProps {
  store?: Store;
}

export const IHRPlayerBar = inject("store")(
  observer(({ store }: IHRPlayerBarProps) => {
    if (!store) {
      return null;
    }

    if (store.site.playerIsNotActive) {
      const { override } = store.player.defaultStation(store);
      log.info(`Player is not active for id: ${override.id}; skipping render`);
      return null;
    }

    const { player } = store;
    const iframeUrl = player.iframeUrl;
    const [showError, setShowError] = useState<boolean>(false);
    const playerJsRef = useRef<PlayerJs.Player | null>(null);
    const { t } = useTranslation();

    const registerEvents = useCallback(() => {
      const iframeEl = document.querySelector("#ihr-player-bar-frame") as HTMLIFrameElement;
      if (iframeUrl && iframeEl) {
        iframeEl.onclick = () => {
          const playerjs = new PlayerJs.Player(iframeEl);
          playerJsRef.current = playerjs;
          playerjs.on(ListenLive.READY, (data: PlayerJs.readyData) => {
            playerjs.ready(data);
            player.onReady(store);
          });
          playerjs.on(ListenLive.PLAY, () => player.onPlay());
          playerjs.on(ListenLive.PAUSE, () => player.onPause());
          playerjs.on(ListenLive.ENDED, () => player.onPause());
          playerjs.on(ListenLive.PROGRESS, () => player.onProgress());
          playerjs.on(ListenLive.ERROR, (data: PlayerJs.errorSdk) => {
            if (Math.abs(data.code) > 0) {
              log.error(data.msg);
            }
            setShowError(true);
            player.onError(store);
          });
        };
        iframeEl.click();
      }
    }, [store, player, iframeUrl, setShowError]);

    const handleExternalAction = useCallback(
      (event: ListenLiveEvent) => {
        if (iframeUrl) {
          switch (event.action) {
            case ListenLive.PLAY:
              playerJsRef?.current?.play();
              break;
            case ListenLive.PAUSE:
              playerJsRef?.current?.pause();
              break;
            default:
              log.debug("Invalid action");
              break;
          }
        }
      },
      [iframeUrl],
    );

    const handleDismissToast = useCallback(() => {
      setShowError(false);
    }, [setShowError]);

    const handleIframeError = useCallback(() => {
      setShowError(true);
      player.onError(store);
    }, [player, store, setShowError]);

    useEffect(() => {
      if (!player.loaded) {
        player.init(store);
        player.externalAction.subscribe(handleExternalAction);
        return;
      }
      if (iframeUrl) {
        log.debug("Registering IHR Player Events");
        registerEvents();
        if (player.externalAction.count) {
          player.externalAction.unsubscribe(handleExternalAction);
          player.externalAction.subscribe(handleExternalAction);
        }
      }
      return () => player.externalAction.unsubscribe(handleExternalAction);
    }, [store, player, iframeUrl, handleExternalAction, registerEvents]);

    return iframeUrl ? (
      <>
        <div className={styles.ihrPlayerBar}>
          <div className={styles.ihrPlayerBarContent}>
            <iframe
              onError={handleIframeError}
              id="ihr-player-bar-frame"
              title="Listen on iHeartRadio"
              src={iframeUrl}
              allow="autoplay"
              allow-same-origin="true"
              allow-scripts="true"
            />
          </div>
        </div>
        {showError && (
          <AppTrayInjector>
            <Toast
              open={true}
              title={t("sorry")}
              message={t("toast_error_message")}
              themeOverride={true}
              callback={handleDismissToast}
            >
              <p>
                <MagicLink to="https://help.iheart.com/hc/en-us" external={true} context="ihr-player-bar">
                  {t("report_this_issue")}
                </MagicLink>
              </p>
            </Toast>
          </AppTrayInjector>
        )}
      </>
    ) : null;
  }),
);

export default IHRPlayerBar;
